import { NgModule, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { environment } from '../environments/environment';
import { AuthService } from './auth.service';
import { HubSpotConfigurationService } from './hubspot-configuration.service';
import { clearHubSpotWidget, initializeHubSpotWidget } from './hubspot-widget';

declare let window: any;

@NgModule()
export class HubSpotModule implements OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private routeTrackingSubscription: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private hubspotConfigurationService: HubSpotConfigurationService,
    private router: Router
  ) {
    this.authService.user
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged((previous, current) => previous && current && previous.email === current.email),
        switchMap(() => {
          if (!environment.HUBSPOT_ENABLED || !environment.HUBSPOT_PORTAL_ID) {
            this.clearWidget();
            return EMPTY;
          }

          return this.hubspotConfigurationService.getHubSpotConfiguration().pipe(
            switchMap((hubspotConfiguration) => {
              if (!hubspotConfiguration || !hubspotConfiguration.identificationToken) {
                this.clearWidget();
                return EMPTY;
              }

              return of(hubspotConfiguration);
            }),
            catchError((error) => {
              console.error('Unable to identify the current user to HubSpot', error);
              this.clearWidget();
              return EMPTY;
            })
          );
        })
      )
      .subscribe((hubspotConfiguration) =>
        this.initializeWidget(
          environment.HUBSPOT_PORTAL_ID,
          hubspotConfiguration.identificationEmail,
          hubspotConfiguration.identificationToken
        )
      );
  }

  public ngOnDestroy(): void {
    this.clearWidget();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initializeWidget(portalId: string, identificationEmail: string, identificationToken: string): void {
    initializeHubSpotWidget({
      identificationEmail,
      identificationToken,
      portalId
    }, () => this.startRouteTracking());
  }

  private startRouteTracking(): void {
    if (this.routeTrackingSubscription) {
      return;
    }

    this.routeTrackingSubscription = this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter((event) => event instanceof NavigationEnd),
        map((event) =>
          (event as NavigationEnd).url
            .replace(/[0-9]+/g, 'X')
            .split('/(')[0]
            .replace(/\/X$/, '')
        ),
        distinctUntilChanged()
      )
      .subscribe(() => window.HubSpotConversations && window.HubSpotConversations.widget.refresh());
  }

  private clearWidget(): void {
    if (this.routeTrackingSubscription) {
      this.routeTrackingSubscription.unsubscribe();
      this.routeTrackingSubscription = null;
    }

    clearHubSpotWidget();
  }
}
